import { ScanSummary } from './types';
import { runUXScan } from './uxScan';
import { runPWAScan } from './pwaScan';
import { runSecurityScan } from './securityScan';
import { runSecurityFunctionsScan } from './securityFunctionsScan';
import { runViralScan } from './viralScan';
import { runWordlistScan } from './wordlistScan';
import { runPuzzleRandomizationScan } from './puzzleRandomizationScan';
import { runDeterministicPuzzleScan } from './deterministicPuzzleScan';
import { runArchiveRandomizationScan } from './archiveRandomizationScan';
import { runBrandingScan } from './brandingScan';
import { runCloudLocalSyncScan } from './cloudLocalSyncScan';
import { runDBStatsIntegrityScan } from './dbStatsIntegrityScan';
import { runPWAInstallScan } from './pwaInstallScan';
import { runPerformanceScan } from './performanceScan';
import { runAccessibilityScan } from './accessibilityScan';
import { runMobileLayoutScan } from './mobileLayoutScan';
import { runGameFlowScan } from './gameFlowScan';
import { runDataIntegrityScan } from './dataIntegrityScan';
import { runMemoryLeakScan } from './memoryLeakScan';
import { runVisualRegressionScan } from './visualRegressionScan';

export interface MegaScanResult {
  scans: ScanSummary[];
  totalProblemsFound: number;
  totalProblemsFixed: number;
  timestamp: Date;
}

export async function runMegaScan(
  autoFix: boolean = false,
  onProgress?: (scanName: string) => void
): Promise<MegaScanResult> {
  console.group('🔍 MEGA-SCAN: Running All Scans Sequentially');
  console.log(`Starting comprehensive scan suite... (Auto-Fix: ${autoFix ? 'ON' : 'OFF'})\n`);
  
  const startTime = Date.now();
  
  // Run all scans sequentially with progress callbacks
  onProgress?.('UX Scan');
  const uxSummary = runUXScan();
  
  onProgress?.('PWA Scan');
  const pwaSummary = await runPWAScan();
  
  onProgress?.('Security Scan');
  const securitySummary = runSecurityScan(autoFix);
  
  onProgress?.('Security Functions Scan');
  const securityFunctionsSummary = await runSecurityFunctionsScan(autoFix);
  
  onProgress?.('Viral Scan');
  const viralSummary = runViralScan(autoFix);
  
  onProgress?.('Wordlist Scan');
  const wordlistSummary = runWordlistScan(autoFix);
  
  onProgress?.('Puzzle Randomization Scan');
  const puzzleRandomizationSummary = runPuzzleRandomizationScan();
  
  onProgress?.('Deterministic Puzzle Scan');
  const deterministicSummary = runDeterministicPuzzleScan(autoFix);
  
  onProgress?.('Archive Randomization Scan');
  const archiveRandomizationSummary = runArchiveRandomizationScan();
  
  onProgress?.('Branding Scan');
  const brandingSummary = runBrandingScan();
  
  onProgress?.('Cloud-Local Sync Scan');
  const cloudSyncSummary = await runCloudLocalSyncScan();
  
  onProgress?.('DB Stats Integrity Scan');
  const dbStatsSummary = await runDBStatsIntegrityScan();
  
  onProgress?.('PWA Install Scan');
  const pwaInstallSummary = runPWAInstallScan();
  
  onProgress?.('Performance Scan');
  const performanceSummary = runPerformanceScan();
  
  onProgress?.('Accessibility Scan');
  const accessibilitySummary = runAccessibilityScan();
  
  onProgress?.('Mobile Layout Scan');
  const mobileLayoutSummary = runMobileLayoutScan();
  
  onProgress?.('Game Flow Scan');
  const gameFlowSummary = runGameFlowScan();
  
  onProgress?.('Data Integrity Scan');
  const dataIntegritySummary = await runDataIntegrityScan();
  
  onProgress?.('Memory Leak Scan');
  const memoryLeakSummary = runMemoryLeakScan();
  
  onProgress?.('Visual Regression Scan');
  const visualRegressionSummary = runVisualRegressionScan();
  
  onProgress?.('Finalizing...');
  
  const scans = [
    uxSummary, 
    pwaSummary, 
    securitySummary,
    securityFunctionsSummary,
    viralSummary, 
    wordlistSummary, 
    puzzleRandomizationSummary, 
    deterministicSummary,
    archiveRandomizationSummary,
    brandingSummary,
    cloudSyncSummary,
    dbStatsSummary,
    pwaInstallSummary,
    performanceSummary,
    accessibilitySummary,
    mobileLayoutSummary,
    gameFlowSummary,
    dataIntegritySummary,
    memoryLeakSummary,
    visualRegressionSummary
  ];
  
  // Calculate totals
  const totalProblemsFound = scans.reduce(
    (sum, scan) => sum + scan.failCount + scan.warnCount,
    0
  );
  
  const totalProblemsFixed = scans.reduce(
    (sum, scan) => sum + scan.results.filter(r => r.autoFixed).length,
    0
  );
  
  const endTime = Date.now();
  const duration = ((endTime - startTime) / 1000).toFixed(2);
  
  console.log('\n' + '='.repeat(60));
  console.log('📊 MEGA-SCAN SUMMARY');
  console.log('='.repeat(60));
  console.log(`✅ Total Passed: ${scans.reduce((sum, s) => sum + s.passCount, 0)}`);
  console.log(`⚠️  Total Warnings: ${scans.reduce((sum, s) => sum + s.warnCount, 0)}`);
  console.log(`❌ Total Failed: ${scans.reduce((sum, s) => sum + s.failCount, 0)}`);
  console.log(`ℹ️  Total Info: ${scans.reduce((sum, s) => sum + s.infoCount, 0)}`);
  console.log(`🔧 Auto-Fixed: ${totalProblemsFixed}`);
  console.log(`⏱️  Duration: ${duration}s`);
  console.log('='.repeat(60));
  
  // Detailed breakdown by scan
  console.log('\n📋 Scan Breakdown:');
  scans.forEach(scan => {
    console.log(`\n${scan.scanName}:`);
    console.log(`  ✅ Pass: ${scan.passCount} | ⚠️  Warn: ${scan.warnCount} | ❌ Fail: ${scan.failCount} | ℹ️  Info: ${scan.infoCount}`);
  });
  
  console.groupEnd();
  
  return {
    scans,
    totalProblemsFound,
    totalProblemsFixed,
    timestamp: new Date()
  };
}
